import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FormUtilsService {
  constructor() {}

  /**
   * Validates a form and all its child controls recursively
   * @param form The form to validate
   * @param markAsTouched Whether to mark all controls as touched
   * @returns True if the form is valid, false otherwise
   */
  validateFormRecursively(form: FormGroup | AbstractControl | null, markAsTouched = true): boolean {
    if (!form) return false;

    // Mark all controls as touched to show validation errors
    if (markAsTouched) {
      this.markFormGroupTouched(form);
    }

    // For FormGroups, check all child controls
    if (form instanceof FormGroup) {
      // Start with the form's own validity
      let isValid = form.valid;

      // Check each control in the form group
      Object.keys(form.controls).forEach((key) => {
        const control = form.get(key);
        if (!control) return;

        // Check if the individual control is valid
        if (!control.valid) {
          isValid = false;
        }

        // Recursively validate nested form groups and arrays
        if (control instanceof FormGroup || control instanceof FormArray) {
          const childValid = this.validateFormRecursively(control, false); // Don't mark as touched again
          isValid = isValid && childValid;
        }
      });

      // Log validation status for debugging
      const validationStatus = this.getFormValidationStatus(form);

      return isValid;
    }
    // For FormArrays, check all items in the array
    else if (form instanceof FormArray) {
      // Start with the form array's own validity
      let isValid = form.valid;

      // Check each control in the form array
      form.controls.forEach((control, index) => {
        // Check if the individual control is valid
        if (!control.valid) {
          isValid = false;
        }

        // Recursively validate nested form groups and arrays
        if (control instanceof FormGroup || control instanceof FormArray) {
          const childValid = this.validateFormRecursively(control, false); // Don't mark as touched again
          isValid = isValid && childValid;
        }
      });

      // Log validation status for debugging
      const validationStatus = this.getFormValidationStatus(form);

      return isValid;
    }
    // For simple form controls
    else {
      // Check if the control has a value
      if (form.value === null || form.value === undefined || form.value === '') {
        return false;
      }

      // Log validation status for debugging
      const validationStatus = this.getFormValidationStatus(form);

      return form.valid;
    }
  }

  /**
   * Recursively marks all controls in a form group as touched
   * @param formGroup The form group to mark as touched
   */
  markFormGroupTouched(control: AbstractControl): void {
    control.markAsTouched({ onlySelf: true });

    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach((key) => {
        const ctrl = control.get(key);
        if (ctrl) {
          this.markFormGroupTouched(ctrl);
        }
      });
    } else if (control instanceof FormArray) {
      control.controls.forEach((ctrl) => {
        this.markFormGroupTouched(ctrl);
      });
    }
  }

  /**
   * Gets the validation status of a form and its controls
   * @param form The form to check
   * @returns An object with the validation status of the form and its controls
   */
  getFormValidationStatus(form: AbstractControl): any {
    if (form instanceof FormGroup) {
      const status: any = {
        valid: form.valid,
        controls: {}
      };

      Object.keys(form.controls).forEach((key) => {
        const control = form.get(key);
        if (control) {
          status.controls[key] = {
            valid: control.valid,
            errors: control.errors,
            ...(control instanceof FormGroup || control instanceof FormArray ? { details: this.getFormValidationStatus(control) } : {})
          };
        }
      });

      return status;
    } else if (form instanceof FormArray) {
      return {
        valid: form.valid,
        controls: form.controls.map((control) => this.getFormValidationStatus(control))
      };
    } else {
      return {
        valid: form.valid,
        errors: form.errors
      };
    }
  }
}
